// ── Maestros (Catalogs) Models ──
// Shared types for catalog CRUD pages.

// ─── Roles ───
export interface RoleItem {
  id: string
  code: string
  name: string
  description?: string | null
  isSystemRole: boolean
  isExternalRole: boolean
  /**
   * Owning tenant for custom roles. Null for global roles (visible to every
   * tenant; only the platform tenant may mutate them).
   */
  tenantId?: string | null
}

export interface CreateRoleRequest {
  code: string
  name: string
  description?: string | null
  isExternalRole: boolean
}

export type UpdateRoleRequest = CreateRoleRequest

export interface GetRolesParams {
  page?: number
  pageSize?: number
  search?: string
  isSystemRole?: boolean
  isExternalRole?: boolean
}

// ─── Mineral Types ───
export interface MineralTypeItem {
  id: string
  code: string
  name: string
  description?: string | null
  isActive: boolean
  isPayable?: boolean
  isPenalty?: boolean
  defaultGradeUnitId?: string | null
}

export interface CreateMineralTypeRequest {
  code: string
  name: string
  description?: string | null
  isActive: boolean
}

export type UpdateMineralTypeRequest = CreateMineralTypeRequest

export interface GetMineralTypesParams {
  page?: number
  pageSize?: number
  search?: string
  isActive?: boolean
}

// ─── Measurement Units ───
export enum MeasurementCategory {
  Weight = 1,
  Volume = 2,
  Length = 3,
  Grade = 4,
  Moisture = 5,
}

export interface MeasurementUnitItem {
  id: string
  name: string
  abbreviation: string
  category: MeasurementCategory
  conversionFactorToBase: number
  isBaseUnit: boolean
  isActive: boolean
  sortOrder: number
}

export interface CreateMeasurementUnitRequest {
  name: string
  abbreviation: string
  category: MeasurementCategory
  conversionFactorToBase: number
  isBaseUnit: boolean
  isActive: boolean
  sortOrder: number
}

export type UpdateMeasurementUnitRequest = CreateMeasurementUnitRequest

export interface GetMeasurementUnitsParams {
  page?: number
  pageSize?: number
  search?: string
  category?: MeasurementCategory
  isActive?: boolean
}

export interface ConvertMeasurementRequest {
  fromUnitId: string
  toUnitId: string
  value: number
}

export interface ConvertMeasurementResponse {
  outputValue: number
}

// ─── Validaciones Recepcion ───
export type ValidacionSeveridad = 'Rojo' | 'Amarillo'

export type ValidacionFuente =
  | 'ReinfoLocal'
  | 'SunatScraping'
  | 'RecpoLocal'
  | 'BaseDatosInterna'
  | 'Manual'
  | 'PostGIS'

export interface ValidationConfigurationItem {
  id: string
  codigo: string
  nombre: string
  descripcion?: string | null
  grupoNombre: string
  grupoIcono?: string | null
  grupoOrden: number
  orden: number
  severidad: ValidacionSeveridad
  fuente: ValidacionFuente
  activa: boolean
  aplicaPN: boolean
  aplicaPJ: boolean
  umbralDias?: number | null
  umbralPorcentaje?: number | null
  metadataJson?: string | null
}

export interface CreateValidacionRequest {
  codigo: string
  nombre: string
  descripcion?: string | null
  grupoNombre: string
  grupoIcono?: string | null
  grupoOrden: number
  orden: number
  severidad: ValidacionSeveridad
  fuente: ValidacionFuente
  activa: boolean
  aplicaPN: boolean
  aplicaPJ: boolean
  umbralDias?: number | null
  umbralPorcentaje?: number | null
  metadataJson?: string | null
}

export type UpdateValidacionRequest = CreateValidacionRequest

export interface GetValidacionesParams {
  page?: number
  pageSize?: number
  search?: string
  grupoNombre?: string
  activa?: boolean
  aplicaPN?: boolean
  aplicaPJ?: boolean
}

// ─── Lookup-only catalogs (int id + description) ───
export interface LookupItem {
  id: number
  description: string
}

export interface UpdateLookupRequest {
  description: string
}

// ─── Tipos de Combustible (factor de emisión MINAM, huella de carbono) ───
// `type` (no interface) para satisfacer la restricción Record<string, unknown>
// de CatalogoBasePage.
export type FuelTypeItem = {
  id: number
  description: string
  emissionFactorKgCo2e: number
  emissionUnit: string
  emissionSource: string
}

export interface UpdateFuelTypeRequest {
  emissionFactorKgCo2e: number
  emissionUnit: string
  emissionSource: string
}

// ─── Component Categories ───
// CRUD lookup grouping ComponentTypes by use-case (explotacion vs beneficio).
// Backend logic only branches on the stable `code`.
export interface ComponentCategoryItem {
  id: string
  code: string
  name: string
  description?: string | null
  sortOrder: number
}

export interface CreateComponentCategoryRequest {
  code: string
  name: string
  description?: string | null
  sortOrder: number
}

export type UpdateComponentCategoryRequest = CreateComponentCategoryRequest

export interface GetComponentCategoriesParams {
  page?: number
  pageSize?: number
  search?: string
}

// ─── Component Types ───
export interface ComponentTypeItem {
  id: string
  code: string
  name: string
  description?: string | null
  sortOrder: number
  categoryId: string
  categoryCode?: string | null
  categoryName?: string | null
  tierId: string
  tierCode?: string | null
  tierName?: string | null
}

export interface CreateComponentTypeRequest {
  code: string
  name: string
  description?: string | null
  sortOrder: number
  categoryId: string
  tierId: string
}

export type UpdateComponentTypeRequest = CreateComponentTypeRequest

export interface GetComponentTypesParams {
  page?: number
  pageSize?: number
  search?: string
  /** Filter by parent category code (e.g. "explotacion" or "beneficio"). */
  categoryCode?: string
  categoryId?: string
  /** Filter by tier code (e.g. "principal" or "secundario"). */
  tierCode?: string
}

// ─── Component Tiers ───
// CRUD lookup classifying ComponentTypes by importance within their activity
// (principal vs secundario). Orthogonal to ComponentCategory.
export interface ComponentTierItem {
  id: string
  code: string
  name: string
  description?: string | null
  sortOrder: number
}

export interface CreateComponentTierRequest {
  code: string
  name: string
  description?: string | null
  sortOrder: number
}

export type UpdateComponentTierRequest = CreateComponentTierRequest

export interface GetComponentTiersParams {
  page?: number
  pageSize?: number
  search?: string
}
